import Button from "antd/es/button";
import Input from "antd/es/input";
import PageHeader from "antd/es/page-header";
import Card from "antd/lib/card";
import DatePicker from "antd/lib/date-picker";
import Form from "antd/lib/form";
import { Col, Row } from "antd/lib/grid";
import Image from "antd/lib/image";
import Space from "antd/lib/space";
import Title from "antd/lib/typography/Title";
import { AxiosError, AxiosResponse } from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import VuiContainer from "../../../../@vodea/vodea-ui/components/Container";
import CustomerRepository from "../../../../repositories/CustomerRepository";
import VuiUploadImages from "../../../../@vodea/vodea-ui/components/UploadImages";
import _ from "lodash";
import {
  checkIsValidPhone,
  handleBackendError,
  openNotification,
} from "../../../../functions/global";
import Accessible from "../../../../@vodea/vodea-ui/components/Accessible";
import {
  ACCESS_PERMISSION,
  DEFAULT_PHONE_COUNTRY_CODE,
} from "../../../../constant";
import Spin from "antd/lib/spin";
import PhoneInput from "../../../../@vodea/vodea-ui/components/PhoneInput";
import { Customer } from "../../../../entities/customer";
import { Radio } from "antd";
import TextArea from "antd/es/input/TextArea";
import { useFormErrors } from "../../../../@vodea/utilities/hooks";
import CustomerInviteRepository from "../../../../repositories/CustomerInviteRepository";

let title = "Customer";

const AppCustomerForm: React.FC<any> = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const pageTitle = id
    ? t("common.text.editItem", { item: title })
    : t("common.text.addNewItem", { item: title });

  // Form
  const [form] = Form.useForm();
  const { getFormError, getFormValidateStatus, setFormError, clearFormError } =
    useFormErrors();
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<Customer | null>(null);
  const photo = Form.useWatch("photo", form);

  useEffect(() => {
    if (id) {
      getData();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getData = async () => {
    await CustomerRepository.show(id, { with: ["photo", "child.photo"] })
      .then((res: AxiosResponse) => {
        const data = res.data?.data || {};
        const modified: any = {
          ...data,
          date_of_birth: moment(data.date_of_birth),
          phone: {
            code: data?.phone_country
              ? Number(data?.phone_country)
              : DEFAULT_PHONE_COUNTRY_CODE,
            phone: data?.phone_number,
          },
          photo: data.photo?.url,
          child: data.child.map((item: any) => {
            return {
              ...item,
              date_of_birth: moment(item.date_of_birth),
              photo: item.photo?.url,
            };
          }),
        };

        setData(modified);

        form.setFieldsValue({
          ...modified,
        });
      })
      .catch((e: AxiosError) => {
        console.log(e);
      });
  };

  const [inviteLoading, setInviteLoading] = useState(false);
  const onInvite = async () => {
    setInviteLoading(true);

    await CustomerInviteRepository.create(id)
      .then(() => {
        openNotification("success", "Successfully invite this customer");
      })
      .catch((err: AxiosError) => {
        handleBackendError(err, t("notification.error.default"));
      })
      .finally(() => {
        setInviteLoading(false);
      });
  };

  const onFinish = async (data: any) => {
    setIsLoading(true);

    const payload = {
      ...data,
      phone_country: String(data?.phone?.code),
      phone_number: data?.phone?.phone ?? "",
      photo_id: _.get(data, "photo_id[0].id"),
    };

    (id
      ? CustomerRepository.update(id, payload)
      : CustomerRepository.create(payload)
    )
      .then(() => {
        navigate(-1);
        openNotification(
          "success",
          t(`notification.success.${id ? "updateItem" : "createItem"}`, {
            item: title,
          }),
        );
      })
      .catch((err: AxiosError) => {
        setFormError(err);
        handleBackendError(err, t("notification.error.default"));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (data) {
      if (data?.email && data?.phone_number && !data?.can_invite) {
        setIsDisabled(true);
      }
    }
  }, [data]);

  return (
    <>
      <Helmet>
        <title>{title} Detail</title>
      </Helmet>
      <VuiContainer>
        <PageHeader
          className="default-page-header"
          onBack={() => navigate(-1)}
          title={pageTitle}
          extra={[
            <>
              {data?.can_invite && (
                <Spin spinning={inviteLoading}>
                  <Button
                    className="vui-btn"
                    onClick={onInvite}
                    type="primary"
                    size={"large"}
                  >
                    Invite
                  </Button>
                </Spin>
              )}
            </>,
          ]}
        />

        <Form
          form={form}
          layout={"vertical"}
          onFinish={onFinish}
          onValuesChange={clearFormError}
        >
          <Row gutter={[16, 16]}>
            <Col className="gutter-row" span={24}>
              <Card title={""} className="mb16">
                <Space
                  style={{
                    width: "100%",
                  }}
                  direction="vertical"
                  size={0}
                >
                  <div className="vui-form-group type-column">
                    <div className="input-section">
                      {id ? (
                        <Form.Item label="Photo" name="photo">
                          <Image width={140} src={photo} />
                        </Form.Item>
                      ) : (
                        <Form.Item label="Photo" name="photo_id">
                          <VuiUploadImages isUploadFirst />
                        </Form.Item>
                      )}
                    </div>
                  </div>

                  <Row gutter={[16, 0]} style={{ width: "100%" }}>
                    <Col md={8} sm={24} style={{ width: "100%" }}>
                      <div className="vui-form-group type-column">
                        <div className="input-section">
                          <Form.Item
                            name="name"
                            label={"Name"}
                            rules={[
                              {
                                required: true,
                                message: t("validation.required", {
                                  item: "Name",
                                }),
                              },
                            ]}
                          >
                            <Input
                              disabled={isDisabled}
                              size={"large"}
                              placeholder={t("common.text.input", {
                                item: "Name",
                              })}
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </Col>

                    <Col md={8} sm={24} style={{ width: "100%" }}>
                      <div className="vui-form-group type-column">
                        <div className="input-section">
                          <Form.Item
                            name="date_of_birth"
                            label={"Date of Birth"}
                            rules={[
                              {
                                required: true,
                                message: t("validation.required", {
                                  item: "Date of Birth",
                                }),
                              },
                            ]}
                          >
                            <DatePicker
                              disabled={isDisabled}
                              format="DD-MM-YYYY"
                              style={{ width: 400, maxWidth: "100%" }}
                              size={"large"}
                              placeholder={t("common.text.input", {
                                item: "Date of Birth",
                              })}
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </Col>

                    <Col md={8} sm={24} style={{ width: "100%" }}>
                      <div className="vui-form-group type-column">
                        <div className="input-section">
                          <Form.Item
                            name={"gender"}
                            label="Gender"
                            rules={[
                              {
                                required: true,
                                message: t("validation.required", {
                                  item: "Gender",
                                }),
                              },
                            ]}
                          >
                            <Radio.Group disabled={isDisabled}>
                              <Radio value={"Laki - laki"}>Laki - laki</Radio>
                              <Radio value={"Perempuan"}>Perempuan</Radio>
                            </Radio.Group>
                          </Form.Item>
                        </div>
                      </div>
                    </Col>

                    <Col md={12} sm={24} style={{ width: "100%" }}>
                      <div className="vui-form-group type-column">
                        <div className="input-section">
                          <Form.Item
                            name="email"
                            label={"Email"}
                            validateStatus={getFormValidateStatus("email")}
                            help={getFormError("email")}
                          >
                            <Input
                              disabled={isDisabled}
                              size={"large"}
                              placeholder={t("common.text.input", {
                                item: "Email",
                              })}
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </Col>

                    <Col md={12} sm={24} style={{ width: "100%" }}>
                      <div className="vui-form-group type-column">
                        <div className="input-section">
                          <Form.Item
                            initialValue={{
                              code: data?.phone_country
                                ? Number(data?.phone_country)
                                : DEFAULT_PHONE_COUNTRY_CODE,
                            }}
                            rules={[
                              {
                                validator: (_, value) =>
                                  checkIsValidPhone(t, value),
                              },
                            ]}
                            name={"phone"}
                            label="Phone Number"
                            validateStatus={getFormValidateStatus(
                              "phone_number",
                            )}
                            help={getFormError("phone_number")}
                          >
                            <PhoneInput
                              size="large"
                              disabled={isDisabled}
                              placeholder="Enter your phone number"
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </Col>

                    <Col sm={24} style={{ width: "100%" }}>
                      <div className="vui-form-group type-column">
                        <div className="input-section">
                          <Form.Item
                            name="address"
                            label={"Address"}
                            rules={[
                              {
                                required: true,
                                message: t("validation.required", {
                                  item: "Address",
                                }),
                              },
                            ]}
                          >
                            <TextArea
                              disabled={isDisabled}
                              size={"large"}
                              placeholder={t("common.text.input", {
                                item: "Address",
                              })}
                              rows={3}
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </Col>
                  </Row>

                  <Form.List name="child">
                    {(fields) =>
                      fields.length > 0 && (
                        <Card title="Relationship With">
                          <Space
                            style={{
                              width: "100%",
                            }}
                            direction="vertical"
                            size={15}
                          >
                            {fields.map(({ key, name, ...restField }) => (
                              <Card title={""} key={key}>
                                <Row align={"middle"} justify={"space-between"}>
                                  <Title level={5}>{name + 1}</Title>
                                </Row>
                                <div className="vui-form-group type-column">
                                  <div className="input-section">
                                    <Form.Item label="Photo" name="photo">
                                      <Image width={140} src={photo} />
                                    </Form.Item>
                                  </div>
                                </div>

                                <Row gutter={[16, 0]}>
                                  <Col md={8} sm={24}>
                                    <div className="vui-form-group type-column">
                                      <div className="input-section">
                                        <Form.Item
                                          {...restField}
                                          name={[name, "name"]}
                                          label={"Name"}
                                          rules={[
                                            {
                                              required: true,
                                              message: t(
                                                "validation.required",
                                                {
                                                  item: "Name",
                                                },
                                              ),
                                            },
                                          ]}
                                        >
                                          <Input
                                            disabled
                                            size={"large"}
                                            placeholder={t(
                                              "common.text.input",
                                              {
                                                item: "Name",
                                              },
                                            )}
                                          />
                                        </Form.Item>
                                      </div>
                                    </div>
                                  </Col>

                                  <Col md={8} sm={24}>
                                    <div className="vui-form-group type-column">
                                      <div className="input-section">
                                        <Form.Item
                                          {...restField}
                                          name={[name, "date_of_birth"]}
                                          label={"Date of Birth"}
                                          rules={[
                                            {
                                              required: true,
                                              message: t(
                                                "validation.required",
                                                {
                                                  item: "Date of Birth",
                                                },
                                              ),
                                            },
                                          ]}
                                        >
                                          <DatePicker
                                            disabled
                                            disabledDate={(current) => {
                                              let customDate =
                                                moment().format("YYYY-MM-DD");
                                              return (
                                                current &&
                                                current <
                                                  moment(
                                                    customDate,
                                                    "YYYY-MM-DD",
                                                  )
                                              );
                                            }}
                                            format="DD-MM-YYYY"
                                            style={{
                                              width: 400,
                                              maxWidth: "100%",
                                            }}
                                            size={"large"}
                                            placeholder={t(
                                              "common.text.input",
                                              {
                                                item: "Date of Birth",
                                              },
                                            )}
                                          />
                                        </Form.Item>
                                      </div>
                                    </div>
                                  </Col>

                                  <Col md={8} sm={24}>
                                    <div className="vui-form-group type-column">
                                      <div className="input-section">
                                        <Form.Item
                                          {...restField}
                                          name={[name, "gender"]}
                                          label={"Gender"}
                                          rules={[
                                            {
                                              required: true,
                                              message: t(
                                                "validation.required",
                                                {
                                                  item: "Gender",
                                                },
                                              ),
                                            },
                                          ]}
                                        >
                                          <Radio.Group disabled>
                                            <Radio value={"Laki - laki"}>
                                              Laki - laki
                                            </Radio>
                                            <Radio value={"Perempuan"}>
                                              Perempuan
                                            </Radio>
                                          </Radio.Group>
                                        </Form.Item>
                                      </div>
                                    </div>
                                  </Col>

                                  <Col md={12} sm={24}>
                                    <div className="vui-form-group type-column">
                                      <div className="input-section">
                                        <Form.Item
                                          {...restField}
                                          name={[name, "email"]}
                                          label={"Email"}
                                        >
                                          <Input
                                            disabled
                                            size={"large"}
                                            placeholder={t(
                                              "common.text.input",
                                              {
                                                item: "Email",
                                              },
                                            )}
                                          />
                                        </Form.Item>
                                      </div>
                                    </div>
                                  </Col>
                                  <Col md={12} sm={24}>
                                    <div className="vui-form-group type-column">
                                      <div className="input-section">
                                        <Form.Item
                                          {...restField}
                                          name={[name, "phone_number"]}
                                          label={"Phone Number"}
                                        >
                                          <Input
                                            disabled
                                            size={"large"}
                                            placeholder={t(
                                              "common.text.input",
                                              {
                                                item: "Phone Number",
                                              },
                                            )}
                                          />
                                        </Form.Item>
                                      </div>
                                    </div>
                                  </Col>

                                  <Col sm={24}>
                                    <div className="vui-form-group type-column">
                                      <div className="input-section">
                                        <Form.Item
                                          {...restField}
                                          name={[name, "address"]}
                                          label={"Address"}
                                          rules={[
                                            {
                                              required: true,
                                              message: t(
                                                "validation.required",
                                                {
                                                  item: "Address",
                                                },
                                              ),
                                            },
                                          ]}
                                        >
                                          <TextArea
                                            disabled
                                            size={"large"}
                                            placeholder={t(
                                              "common.text.input",
                                              {
                                                item: "Address",
                                              },
                                            )}
                                            rows={3}
                                          />
                                        </Form.Item>
                                      </div>
                                    </div>
                                  </Col>
                                </Row>
                              </Card>
                            ))}
                          </Space>
                        </Card>
                      )
                    }
                  </Form.List>
                </Space>
              </Card>
            </Col>
          </Row>

          <div className="vui-form-btn-group">
            <Button
              className="vui-btn"
              size={"large"}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
            {!isDisabled && (
              <Accessible access={ACCESS_PERMISSION.customer.store}>
                <Spin spinning={isLoading}>
                  <Button
                    className="vui-btn"
                    type="primary"
                    htmlType="submit"
                    size={"large"}
                  >
                    Submit
                  </Button>
                </Spin>
              </Accessible>
            )}
          </div>
        </Form>
      </VuiContainer>
    </>
  );
};

export default AppCustomerForm;
